import {
	APIEvent,
	faro,
	getWebInstrumentations,
	initializeFaro,
	LogLevel,
	Patterns,
	ReactIntegration,
	ReactRouterVersion,
	TransportItem,
	TransportItemType,
} from '@grafana/faro-react';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { Route, useHistory } from 'react-router-dom';
import APP_VERSION from './appVersion';
import instrumentAssets from './instrumentAssets';
import instrumentDevice from './instrumentDevice';
import OfflineBufferedFetchTransport from './offlineTransport';
import scrubPersonalData from './redact';
import { registerFaroInstance } from './telemetry';
import getEnvironment from '../common/environment';

// Backends we own and want distributed traces for. The trace header must be propagated to
// every one of them, otherwise the trace ends at the browser and cannot be joined with the
// backend span. The previous list covered only services-api and mf-asset-api, so calls to
// maintenance/access/vendor/vendor-exp produced orphaned frontend-only traces.
const TRACED_BACKENDS: Patterns = [
	/^https:\/\/[^/]*\.(amh|ah4r)\.com\/(services|mf-asset|mf-maintenance|mf-access|mf-vendor|mf-vendor-exp|mf-work-order)-api\//,
	/^https:\/\/[^/]*\.(amh|ah4r)\.com\/api\//,
	/^https:\/\/localhost:\d+\/api\//,
];

// Third-party traffic that only adds noise (and cost) to traces and resource timings.
// Faro's own collector is included deliberately — without it the transport instruments itself.
const IGNORED_URLS: Patterns = [
	/faro-collector[^/]*\.grafana\.net/,
	/\.i\.posthog\.com/,
	/(app|events|clientstream)\.launchdarkly\.com/,
	/maps\.googleapis\.com/,
	/dl\.boxcloud\.com/,
	/(use|p)\.typekit\.net/,
	/fonts\.(googleapis|gstatic)\.com/,
];

// Known browser/extension noise that is not actionable for us.
const IGNORED_ERRORS: Patterns = [
	/ResizeObserver loop/,
	/Non-Error promise rejection captured/,
	/^AbortError/,
	/Failed to fetch dynamically imported module/,

	// SignalR writes every disconnect to console.error, and the console instrumentation turns
	// those into exceptions. On an app designed to survive losing connectivity these are expected
	// operational events, not faults — and they were 100% of the exception stream on priv, which
	// made the exception count useless as a health signal. The information is not lost: hub
	// lifecycle is reported structurally as `signalr_state_changed` from HubConfigurationProvider.
	/Connection disconnected with error/,
	/Failed to complete negotiation with the server/,
	/Failed to start the connection/,
	/Server timeout elapsed without receiving a message/,
	/WebSocket closed with status code/,
];

// Faro caps a session at 4h (SESSION_EXPIRATION_TIME) but by default drops it after 15 min of
// inactivity and does not persist it across reloads. A field technician's shift is a single
// unit of work — losing the session mid-shift makes "what happened before the sync failed"
// unanswerable, so we opt into sticky sessions for the full 4h Faro allows.
const SESSION_PERSISTENCE_MS = 4 * 60 * 60 * 1000;

function resolveEnvironment(apiUrl: string): string {
	if (process.env.REACT_APP_HOST_TYPE === 'local') {
		return 'local';
	}
	return getEnvironment(apiUrl)?.name ?? 'production';
}

// Runs for every signal. Attaches the device/connectivity state at the moment the signal was
// produced — this is not recoverable after the fact and is the single most useful dimension
// for an offline-first PWA used in the field.
function enrichWithDeviceContext(item: TransportItem<APIEvent>): TransportItem<APIEvent> {
	if (item.type === TransportItemType.TRACE) {
		return item;
	}

	const payload = item.payload as Record<string, unknown> & { context?: Record<string, string> };
	const { connection } = navigator as Navigator & { connection?: { effectiveType?: string } };

	Object.assign(payload, scrubPersonalData(payload));

	// Added after scrubbing — these values are ours and contain nothing to redact.
	payload.context = {
		...payload.context,
		online: String(navigator.onLine),
		connection: connection?.effectiveType ?? 'unknown',
		standalone: String(window.matchMedia('(display-mode: standalone)').matches),
	};

	return item;
}

export interface ObservabilitySettings {
	APP_GRAFANA_FARO_COLLECTOR_URL: string;
	API_URL: string;
}

/**
 * Initialises Faro. Must be called before ReactDOM.render so that failures during app
 * bootstrap (config fetch, micro-frontend bundle loading, auth) are captured — previously
 * Faro started inside a component rendered *after* settings had loaded, so that whole phase
 * was invisible in Grafana.
 *
 * The React Router integration is not part of this call because it needs a history instance
 * that only exists once Router has mounted; see attachRouterInstrumentation.
 */
export function initObservability(settings: ObservabilitySettings): void {
	if (!settings.APP_GRAFANA_FARO_COLLECTOR_URL) {
		return;
	}
	if (faro?.api) {
		return;
	}
	const offlineTransport = new OfflineBufferedFetchTransport({
		url: settings.APP_GRAFANA_FARO_COLLECTOR_URL,
	});

	const instance = initializeFaro({
		transports: [offlineTransport],
		app: {
			name: '4services',
			version: APP_VERSION,
			environment: resolveEnvironment(settings.API_URL),
		},

		sessionTracking: {
			enabled: true,
			persistent: true,
			maxSessionPersistenceTime: SESSION_PERSISTENCE_MS,
		},

		trackResources: true,
		trackWebVitalsAttribution: true,
		trackUserActionsPreview: true,

		consoleInstrumentation: {
			disabledLevels: [LogLevel.DEBUG, LogLevel.TRACE, LogLevel.LOG],
			serializeErrors: true,
		},
		batching: { enabled: true, sendTimeout: 3000, itemLimit: 100 },

		ignoreUrls: IGNORED_URLS,
		ignoreErrors: IGNORED_ERRORS,
		beforeSend: enrichWithDeviceContext,

		instrumentations: [
			...getWebInstrumentations({ captureConsole: true }),

			new TracingInstrumentation({
				instrumentationOptions: {
					propagateTraceHeaderCorsUrls: TRACED_BACKENDS,
				},
			}),
		],
	});

	registerFaroInstance(instance);

	offlineTransport.listen();
	offlineTransport.drain();

	instrumentDevice();
	instrumentAssets();
}

/**
 * Wires Faro's React Router v5 instrumentation (route-change events, view names). Called from
 * a component under Router, because that is the first point a history instance exists. Faro
 * supports registering instrumentations after init, which lets initObservability run before
 * the first render without pulling `history` in as a direct dependency.
 */
export function attachRouterInstrumentation(history: ReturnType<typeof useHistory>): void {
	if (!faro?.api) {
		return;
	}

	const alreadyAttached = faro.instrumentations.instrumentations.some((i) => i.name.includes('react'));
	if (alreadyAttached) {
		return;
	}

	faro.instrumentations.add(
		new ReactIntegration({
			router: {
				version: ReactRouterVersion.V5,
				dependencies: {
					history: history as any,
					Route,
				},
			},
		}),
	);
}

const ID_SEGMENT = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d{2,}/gi;

let currentView: string | undefined;

export function setCurrentView(name: string): void {
	if (!faro?.api) {
		return;
	}

	const normalized = name.replace(ID_SEGMENT, ':id').trim() || 'unknown';
	if (normalized === currentView) {
		return;
	}

	currentView = normalized;
	faro.api.setView({ name: normalized });
}

export default initObservability;
